import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Logo } from "../lib/brand";
import { JURISDICTIONS, JURISDICTION_LIST } from "../data/jurisdictions";
import {
  Handshake, PenLine, Scale, Package, FileText, Sparkles,
  ArrowRight, CheckCircle2, ShieldCheck, Globe2,
} from "lucide-react";

const FEATURE_ICONS = {
  "01": Handshake, "02": PenLine, "03": Scale,
  "04": Package, "05": FileText, "06": Sparkles,
};

const FEATURE_TITLES = {
  "01": { title: "Agreements", example_key: "agreement" },
  "02": { title: "Digital Signatures", example_key: "signature" },
  "03": { title: "Case Management", example_key: "case" },
  "04": { title: "Evidence Management", example_key: "evidence" },
};

export default function JurisdictionLandingPage() {
  const { locale } = useParams();
  const j = JURISDICTIONS[(locale || "").toLowerCase()];
  if (!j) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#2A0F5A]" data-testid={`jurisdiction-page-${j.slug}`}>
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#FAFAF8]/95 backdrop-blur-sm border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo size={30} />
            <span className="font-serif text-2xl leading-none">Marianne</span>
            <span className="label-eyebrow ml-2 hidden sm:inline">by Mahakali Tribunal</span>
          </Link>
          <JurisdictionSwitcher current={j.slug} compact />
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm hover:text-[#7B56C9]">Sign in</Link>
            <Button asChild className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs px-5">
              <Link to="/signup">Get started</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-4xl">
            <div className="label-eyebrow text-[#C5A059] mb-6 flex items-center gap-2.5" data-testid="jurisdiction-badge">
              <span className="text-xl leading-none">{j.flag}</span>
              {j.hero_badge}
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] tracking-tighter leading-[0.98] font-medium">
              Legal work<span className="italic text-[#3D1F85]"> for {j.name}.</span>
            </h1>
            <p className="mt-6 label-eyebrow text-[#4A5568]" data-testid="jurisdiction-tag">{j.hero_tag}</p>
            <p className="mt-8 text-[18px] text-[#2A0F5A]/80 max-w-2xl leading-relaxed">
              {j.tagline}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs px-7 py-6">
                <Link to="/signup" data-testid="jurisdiction-cta-primary">Get started for free</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-none border-[#2A0F5A] text-[#2A0F5A] hover:bg-[#2A0F5A] hover:text-[#FAFAF8] uppercase tracking-widest text-xs px-7 py-6">
                <a href="#features">Explore features</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Examples strip */}
      <section className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {Object.entries(j.examples).map(([key, val]) => (
            <div key={key} data-testid={`example-${key}`}>
              <div className="label-eyebrow text-[#C5A059] mb-1.5">{key}</div>
              <p className="text-sm leading-relaxed">{val}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features with locale-aware notes */}
      <section id="features" className="border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-14">
            <div className="label-eyebrow mb-4">Features for {j.name}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Everything legal. <span className="italic text-[#3D1F85]">Tuned to {j.name}.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-12">
            {Object.entries(j.feature_notes).map(([num, note]) => {
              const Icon = FEATURE_ICONS[num];
              const title = FEATURE_TITLES[num].title;
              return (
                <article key={num} data-testid={`feature-${num}`}>
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="label-eyebrow text-[#C5A059] font-mono">{num}</span>
                    <Icon size={16} className="text-[#C5A059]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-3xl tracking-tight leading-tight mb-3">{title}</h3>
                  <p className="text-[15px] leading-[1.75] text-[#2A0F5A]/85">{note}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-14">
            <div className="label-eyebrow mb-4">Who it serves</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Built for every <span className="italic text-[#3D1F85]">{j.name} legal need.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8">
            {j.audiences.map((a) => (
              <div key={a.title} data-testid={`audience-${a.title.toLowerCase()}`}>
                <div className="font-serif text-xl mb-2 pb-2 border-b border-[#C5A059]/40 inline-block">{a.title}</div>
                <p className="text-[14px] leading-[1.7] text-[#2A0F5A]/80">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem note */}
      <section className="border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="label-eyebrow mb-4">Grounded in local law</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Built with a global perspective. <span className="italic text-[#3D1F85]">Grounded in {j.name}.</span>
            </h2>
            <p className="mt-6 text-[16px] leading-[1.8] text-[#2A0F5A]/85 max-w-2xl">
              {j.ecosystem_note}
            </p>
          </div>
          <div className="lg:col-span-2 flex items-center justify-center">
            <Globe2 size={200} strokeWidth={0.75} className="text-[#C5A059]/30" />
          </div>
        </div>
      </section>

      {/* Switch jurisdiction */}
      <section className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="label-eyebrow mb-4">Looking for a different jurisdiction?</div>
          <JurisdictionSwitcher current={j.slug} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-b border-[#E8E3F0]">
        <div className="max-w-5xl mx-auto px-6 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Give your {j.name} legal work <span className="italic text-[#3D1F85]">a place to belong.</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs px-7 py-6">
              <Link to="/signup" data-testid="final-cta">Get started for free</Link>
            </Button>
          </div>
          <p className="mt-16 font-serif italic text-2xl text-[#C5A059]">Legal work, made human.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-14 text-xs text-[#4A5568] leading-relaxed">
          <p className="mb-3 italic">{j.disclaimer}</p>
          <p>© 2026 Mahakali Tribunal. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export function JurisdictionSwitcher({ current, compact = false }) {
  if (compact) {
    return (
      <nav className="hidden md:flex items-center gap-5 text-sm">
        {JURISDICTION_LIST.map((j) => (
          <Link
            key={j.slug}
            to={`/j/${j.slug}`}
            className={`${j.slug === current ? "text-[#2A0F5A] font-medium" : "text-[#4A5568] hover:text-[#2A0F5A]"} flex items-center gap-1`}
            data-testid={`switch-${j.slug}`}
          >
            <span>{j.flag}</span><span>{j.name}</span>
          </Link>
        ))}
      </nav>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {JURISDICTION_LIST.map((j) => (
        <Link
          key={j.slug}
          to={`/j/${j.slug}`}
          className={`border p-4 ${j.slug === current ? "border-[#2A0F5A] bg-[#FAFAF8]" : "border-[#E8E3F0] hover:border-[#2A0F5A]"}`}
          data-testid={`switch-card-${j.slug}`}
        >
          <div className="text-2xl mb-2">{j.flag}</div>
          <div className="font-serif text-lg">{j.name}</div>
          <div className="label-eyebrow text-[10px] mt-1">{j.hero_tag}</div>
        </Link>
      ))}
    </div>
  );
}
